import { and, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { RwaAssetAvailabilityFilter } from "@repo/ports/v2";
import { useOptionalAppSessionScope } from "~/lib/app-session-scope";
import {
  combineCollectionErrorState,
  resolveCollectionErrorPayload,
} from "~/lib/tanstack-db-collection-errors";
import {
  getAssetDomainCollections,
  getAssetUniverseBySymbolsQueryOptions,
} from "./collections";
import type {
  AssetTokenizedEquityValuationModel,
  AssetUniverseCanonicalModel,
  AssetUniverseProductModel,
  AssetUniverseRouteModel,
  AssetUniverseSymbolLinkModel,
} from "./models";
import { normalizeAssetDomainAssetIds } from "./models";
import { normalizeAssetUniverseSymbols } from "../asset-universe-contract";

const RELATIONSHIP_RANK: Record<
  AssetUniverseRouteModel["relationshipType"],
  number
> = {
  native_asset: 0,
  primary_token: 1,
  wrapped_token: 2,
  bridged_token: 3,
  issuer_representation: 4,
  receipt_token: 8,
  staking_derivative: 9,
  synthetic_token: 10,
};

const CHAIN_RANK = new Map<string, number>([
  ["eip155:8453", 0],
  ["eip155:1", 1],
  ["eip155:42161", 2],
  ["eip155:10", 3],
  ["eip155:137", 4],
  ["solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp", 5],
]);

const CANONICAL_AGGREGATION_RELATIONSHIPS = new Set<
  AssetUniverseRouteModel["relationshipType"]
>([
  "native_asset",
  "primary_token",
  "wrapped_token",
  "bridged_token",
  "issuer_representation",
]);

export function isAssetUniverseCanonicalAggregationRoute(
  route: AssetUniverseRouteModel,
): boolean {
  return CANONICAL_AGGREGATION_RELATIONSHIPS.has(route.relationshipType);
}

export function compareAssetUniverseRoutes(
  left: AssetUniverseRouteModel,
  right: AssetUniverseRouteModel,
): number {
  if (left.isGliderSupported !== right.isGliderSupported) {
    return left.isGliderSupported ? -1 : 1;
  }

  const relationshipOrder =
    RELATIONSHIP_RANK[left.relationshipType] -
    RELATIONSHIP_RANK[right.relationshipType];
  if (relationshipOrder !== 0) {
    return relationshipOrder;
  }

  const leftChainRank =
    CHAIN_RANK.get(`${left.chainNamespace}:${left.chainReference}`) ??
    Number.MAX_SAFE_INTEGER;
  const rightChainRank =
    CHAIN_RANK.get(`${right.chainNamespace}:${right.chainReference}`) ??
    Number.MAX_SAFE_INTEGER;
  if (leftChainRank !== rightChainRank) {
    return leftChainRank - rightChainRank;
  }

  return left.assetId.localeCompare(right.assetId);
}

function mapRowsById<T extends { id: string }>(
  rows: readonly T[],
): Map<string, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

function mapCanonicalsByAssetCanonicalId(
  rows: readonly AssetUniverseCanonicalModel[],
): Map<string, AssetUniverseCanonicalModel> {
  return new Map(rows.map((row) => [row.assetCanonicalId, row]));
}

function mapProductsByProductId(
  rows: readonly AssetUniverseProductModel[],
): Map<number, AssetUniverseProductModel> {
  return new Map(rows.map((row) => [row.productId, row]));
}

function mapRoutesByAssetId(
  rows: readonly AssetUniverseRouteModel[],
): Map<string, AssetUniverseRouteModel[]> {
  const mapped = new Map<string, AssetUniverseRouteModel[]>();

  for (const row of rows) {
    const existing = mapped.get(row.assetId);
    if (existing) {
      existing.push(row);
      continue;
    }
    mapped.set(row.assetId, [row]);
  }

  for (const routes of mapped.values()) {
    routes.sort(compareAssetUniverseRoutes);
  }

  return mapped;
}

function mapRoutesByCanonicalId(
  rows: readonly AssetUniverseRouteModel[],
): Map<string, AssetUniverseRouteModel[]> {
  const mapped = new Map<string, AssetUniverseRouteModel[]>();

  for (const row of rows) {
    const existing = mapped.get(row.assetCanonicalId);
    if (existing) {
      existing.push(row);
      continue;
    }
    mapped.set(row.assetCanonicalId, [row]);
  }

  for (const routes of mapped.values()) {
    routes.sort(compareAssetUniverseRoutes);
  }

  return mapped;
}

function mapTokenizedEquityValuationsByAssetId(
  rows: readonly AssetTokenizedEquityValuationModel[],
): Map<string, AssetTokenizedEquityValuationModel[]> {
  const mapped = new Map<string, AssetTokenizedEquityValuationModel[]>();

  for (const row of rows) {
    const existing = mapped.get(row.assetId);
    if (existing) {
      existing.push(row);
    } else {
      mapped.set(row.assetId, [row]);
    }
  }

  for (const valuations of mapped.values()) {
    valuations.sort((left, right) =>
      left.canonicalId.localeCompare(right.canonicalId),
    );
  }

  return mapped;
}

function mapSymbolLinksBySymbol(
  rows: readonly AssetUniverseSymbolLinkModel[],
): Map<string, AssetUniverseSymbolLinkModel> {
  return new Map(rows.map((row) => [row.symbol, row]));
}

function collectCanonicalIds(input: {
  routes: readonly AssetUniverseRouteModel[];
  symbolLinks: readonly AssetUniverseSymbolLinkModel[];
  valuations: readonly AssetTokenizedEquityValuationModel[];
}): string[] {
  const canonicalIds = new Set<string>();

  for (const route of input.routes) {
    canonicalIds.add(route.assetCanonicalId);
  }

  for (const link of input.symbolLinks) {
    canonicalIds.add(link.assetCanonicalId);
  }

  for (const valuation of input.valuations) {
    canonicalIds.add(valuation.canonicalId);
  }

  return [...canonicalIds].sort((left, right) => left.localeCompare(right));
}

function collectSymbolLinkCanonicalIds(
  symbolLinks: readonly AssetUniverseSymbolLinkModel[],
): string[] {
  return [...new Set(symbolLinks.map((link) => link.assetCanonicalId.trim()))]
    .filter((assetCanonicalId) => assetCanonicalId.length > 0)
    .sort((left, right) => left.localeCompare(right));
}

function normalizeAssetCanonicalIds(
  assetCanonicalIds: readonly string[],
): string[] {
  return [
    ...new Set(
      assetCanonicalIds
        .map((assetCanonicalId) => assetCanonicalId.trim())
        .filter((assetCanonicalId) => assetCanonicalId.length > 0),
    ),
  ].sort((left, right) => left.localeCompare(right));
}

function normalizeAssetClassFilterValues(
  assetClass: string | null | undefined,
): string[] | null {
  const normalized = assetClass?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (normalized === "tradfi") {
    return ["equity", "fund"];
  }

  return [normalized];
}

export function useAssetUniverseCanonicalSlice(input: {
  assetCanonicalIds: readonly string[];
  enabled: boolean;
  sessionScopeToken?: string;
}) {
  const sessionScope = useOptionalAppSessionScope();
  const sessionScopeToken = input.sessionScopeToken ?? sessionScope.scopeToken;
  const normalizedAssetCanonicalIds = useMemo(
    () => normalizeAssetCanonicalIds(input.assetCanonicalIds),
    [input.assetCanonicalIds],
  );
  const collections = useMemo(
    () => getAssetDomainCollections({ sessionScopeToken }),
    [sessionScopeToken],
  );

  const canonicalQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetCanonicalIds.length === 0) {
        return undefined;
      }

      return normalizedAssetCanonicalIds.length === 1
        ? queryBuilder
            .from({
              assetUniverseCanonical: collections.assetUniverseCanonical,
            })
            .where(({ assetUniverseCanonical }) =>
              eq(
                assetUniverseCanonical.assetCanonicalId,
                normalizedAssetCanonicalIds[0]!,
              ),
            )
        : queryBuilder
            .from({
              assetUniverseCanonical: collections.assetUniverseCanonical,
            })
            .where(({ assetUniverseCanonical }) =>
              inArray(
                assetUniverseCanonical.assetCanonicalId,
                normalizedAssetCanonicalIds,
              ),
            );
    },
  });

  const canonicalRows = useMemo(
    () => canonicalQuery.data ?? [],
    [canonicalQuery.data],
  );
  const valuationQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetCanonicalIds.length === 0) {
        return undefined;
      }

      return normalizedAssetCanonicalIds.length === 1
        ? queryBuilder
            .from({
              valuation: collections.assetTokenizedEquityValuations,
            })
            .where(({ valuation }) =>
              eq(valuation.canonicalId, normalizedAssetCanonicalIds[0]!),
            )
        : queryBuilder
            .from({
              valuation: collections.assetTokenizedEquityValuations,
            })
            .where(({ valuation }) =>
              inArray(valuation.canonicalId, normalizedAssetCanonicalIds),
            );
    },
  });
  const valuationRows = useMemo(
    () => valuationQuery.data ?? [],
    [valuationQuery.data],
  );
  const collectionError = combineCollectionErrorState(
    input.enabled && normalizedAssetCanonicalIds.length > 0
      ? [
          collections.assetUniverseCanonical,
          collections.assetTokenizedEquityValuations,
        ]
      : [],
  );

  return {
    assetCanonicalIds: normalizedAssetCanonicalIds,
    canonicalByAssetCanonicalId: useMemo(
      () => mapCanonicalsByAssetCanonicalId(canonicalRows),
      [canonicalRows],
    ),
    clearError: collectionError.clearError,
    error: resolveCollectionErrorPayload({
      error: collectionError.lastError,
      fallbackMessage: "Failed to load asset universe canonical data.",
      isError:
        canonicalQuery.isError ||
        valuationQuery.isError ||
        collectionError.isError,
    }),
    errorCount: collectionError.errorCount,
    isError:
      canonicalQuery.isError ||
      valuationQuery.isError ||
      collectionError.isError,
    isLoading: canonicalQuery.isLoading || valuationQuery.isLoading,
    tokenizedEquityValuationsByAssetId: useMemo(
      () => mapTokenizedEquityValuationsByAssetId(valuationRows),
      [valuationRows],
    ),
  };
}

export function useAssetUniverseSlice(input: {
  assetClass?: string | null;
  assetIds?: readonly string[];
  availabilityFilter?: RwaAssetAvailabilityFilter;
  enabled: boolean;
  hydrate?: boolean;
  hydrationStaleTimeMs?: number;
  sessionScopeToken?: string;
  symbols?: readonly string[];
}) {
  const sessionScope = useOptionalAppSessionScope();
  const sessionScopeToken = input.sessionScopeToken ?? sessionScope.scopeToken;
  const normalizedAssetIds = useMemo(
    () => normalizeAssetDomainAssetIds(input.assetIds ?? []),
    [input.assetIds],
  );
  const normalizedSymbols = useMemo(
    () => normalizeAssetUniverseSymbols(input.symbols ?? []),
    [input.symbols],
  );
  const symbolLinkAssetClassFilter = useMemo(
    () => normalizeAssetClassFilterValues(input.assetClass),
    [input.assetClass],
  );
  const collections = useMemo(
    () => getAssetDomainCollections({ sessionScopeToken }),
    [sessionScopeToken],
  );
  const shouldHydrate = input.hydrate ?? true;
  const hydrationQueryOptions = getAssetUniverseBySymbolsQueryOptions({
    availabilityFilter: input.availabilityFilter,
    assetClass: input.assetClass,
    assetIds: normalizedAssetIds,
    sessionScopeToken,
    symbols: normalizedSymbols,
  });

  const hydrationQuery = useQuery({
    ...hydrationQueryOptions,
    enabled: shouldHydrate && input.enabled && normalizedSymbols.length > 0,
    staleTime: input.hydrationStaleTimeMs ?? hydrationQueryOptions.staleTime,
  });

  const assetIdRouteQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({
              assetUniverseRoute: collections.assetUniverseRoutes,
            })
            .where(({ assetUniverseRoute }) =>
              eq(assetUniverseRoute.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({
              assetUniverseRoute: collections.assetUniverseRoutes,
            })
            .where(({ assetUniverseRoute }) =>
              inArray(assetUniverseRoute.assetId, normalizedAssetIds),
            );
    },
  });

  const valuationQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({
              valuation: collections.assetTokenizedEquityValuations,
            })
            .where(({ valuation }) =>
              eq(valuation.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({
              valuation: collections.assetTokenizedEquityValuations,
            })
            .where(({ valuation }) =>
              inArray(valuation.assetId, normalizedAssetIds),
            );
    },
  });

  const symbolLinkQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedSymbols.length === 0) {
        return undefined;
      }

      return normalizedSymbols.length === 1
        ? queryBuilder
            .from({
              assetUniverseSymbolLink: collections.assetUniverseSymbolLinks,
            })
            .where(({ assetUniverseSymbolLink }) => {
              const symbolPredicate = eq(
                assetUniverseSymbolLink.symbol,
                normalizedSymbols[0]!,
              );
              if (!symbolLinkAssetClassFilter) {
                return symbolPredicate;
              }
              return and(
                symbolPredicate,
                inArray(
                  assetUniverseSymbolLink.assetClass,
                  symbolLinkAssetClassFilter,
                ),
              );
            })
        : queryBuilder
            .from({
              assetUniverseSymbolLink: collections.assetUniverseSymbolLinks,
            })
            .where(({ assetUniverseSymbolLink }) => {
              const symbolPredicate = inArray(
                assetUniverseSymbolLink.symbol,
                normalizedSymbols,
              );
              if (!symbolLinkAssetClassFilter) {
                return symbolPredicate;
              }
              return and(
                symbolPredicate,
                inArray(
                  assetUniverseSymbolLink.assetClass,
                  symbolLinkAssetClassFilter,
                ),
              );
            });
    },
  });

  const symbolLinkRows = useMemo(
    () => symbolLinkQuery.data ?? [],
    [symbolLinkQuery.data],
  );
  const symbolLinkCanonicalIds = useMemo(
    () => collectSymbolLinkCanonicalIds(symbolLinkRows),
    [symbolLinkRows],
  );
  const symbolRouteQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || symbolLinkCanonicalIds.length === 0) {
        return undefined;
      }

      return symbolLinkCanonicalIds.length === 1
        ? queryBuilder
            .from({
              assetUniverseRoute: collections.assetUniverseRoutes,
            })
            .where(({ assetUniverseRoute }) =>
              eq(
                assetUniverseRoute.assetCanonicalId,
                symbolLinkCanonicalIds[0]!,
              ),
            )
        : queryBuilder
            .from({
              assetUniverseRoute: collections.assetUniverseRoutes,
            })
            .where(({ assetUniverseRoute }) =>
              inArray(
                assetUniverseRoute.assetCanonicalId,
                symbolLinkCanonicalIds,
              ),
            );
    },
  });
  const assetIdRouteRows = useMemo(
    () => assetIdRouteQuery.data ?? [],
    [assetIdRouteQuery.data],
  );
  const valuationRows = useMemo(
    () => valuationQuery.data ?? [],
    [valuationQuery.data],
  );
  const routeRows = useMemo(() => {
    const symbolRouteRows = symbolRouteQuery.data ?? [];
    if (symbolRouteRows.length === 0) {
      return assetIdRouteRows;
    }

    const routeById = new Map<string, AssetUniverseRouteModel>();
    for (const row of assetIdRouteRows) {
      routeById.set(row.id, row);
    }
    for (const row of symbolRouteRows) {
      routeById.set(row.id, row);
    }
    return [...routeById.values()];
  }, [assetIdRouteRows, symbolRouteQuery.data]);
  const canonicalIds = useMemo(
    () =>
      collectCanonicalIds({
        routes: routeRows,
        symbolLinks: symbolLinkRows,
        valuations: valuationRows,
      }),
    [routeRows, symbolLinkRows, valuationRows],
  );

  const canonicalQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || canonicalIds.length === 0) {
        return undefined;
      }

      return canonicalIds.length === 1
        ? queryBuilder
            .from({
              assetUniverseCanonical: collections.assetUniverseCanonical,
            })
            .where(({ assetUniverseCanonical }) =>
              eq(assetUniverseCanonical.assetCanonicalId, canonicalIds[0]!),
            )
        : queryBuilder
            .from({
              assetUniverseCanonical: collections.assetUniverseCanonical,
            })
            .where(({ assetUniverseCanonical }) =>
              inArray(assetUniverseCanonical.assetCanonicalId, canonicalIds),
            );
    },
  });

  const productQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || canonicalIds.length === 0) {
        return undefined;
      }

      return canonicalIds.length === 1
        ? queryBuilder
            .from({
              assetUniverseProduct: collections.assetUniverseProducts,
            })
            .where(({ assetUniverseProduct }) =>
              eq(assetUniverseProduct.assetCanonicalId, canonicalIds[0]!),
            )
        : queryBuilder
            .from({
              assetUniverseProduct: collections.assetUniverseProducts,
            })
            .where(({ assetUniverseProduct }) =>
              inArray(assetUniverseProduct.assetCanonicalId, canonicalIds),
            );
    },
  });

  const canonicalRows = useMemo(
    () => canonicalQuery.data ?? [],
    [canonicalQuery.data],
  );
  const productRows = useMemo(
    () => productQuery.data ?? [],
    [productQuery.data],
  );
  const canonicalById = useMemo(
    () => mapRowsById(canonicalRows),
    [canonicalRows],
  );
  const canonicalByAssetCanonicalId = useMemo(
    () => mapCanonicalsByAssetCanonicalId(canonicalRows),
    [canonicalRows],
  );
  const productByProductId = useMemo(
    () => mapProductsByProductId(productRows),
    [productRows],
  );
  const routeCandidatesByAssetId = useMemo(
    () => mapRoutesByAssetId(routeRows),
    [routeRows],
  );
  const routesByCanonicalId = useMemo(
    () => mapRoutesByCanonicalId(routeRows),
    [routeRows],
  );
  const symbolLinkBySymbol = useMemo(
    () => mapSymbolLinksBySymbol(symbolLinkRows),
    [symbolLinkRows],
  );
  const shouldReadRouteCollections =
    normalizedAssetIds.length > 0 || symbolLinkCanonicalIds.length > 0;
  const collectionError = combineCollectionErrorState(
    input.enabled
      ? [
          shouldReadRouteCollections ? collections.assetUniverseRoutes : null,
          normalizedAssetIds.length > 0
            ? collections.assetTokenizedEquityValuations
            : null,
          normalizedSymbols.length > 0
            ? collections.assetUniverseSymbolLinks
            : null,
          canonicalIds.length > 0 ? collections.assetUniverseCanonical : null,
          canonicalIds.length > 0 ? collections.assetUniverseProducts : null,
        ]
      : [],
  );

  return {
    assetIds: normalizedAssetIds,
    canonicalByAssetId: useMemo(() => {
      const mapped = new Map<string, AssetUniverseCanonicalModel>();
      for (const assetId of normalizedAssetIds) {
        const route = routeCandidatesByAssetId
          .get(assetId)
          ?.find(isAssetUniverseCanonicalAggregationRoute);
        if (!route) {
          continue;
        }
        const canonical = canonicalByAssetCanonicalId.get(
          route.assetCanonicalId,
        );
        if (canonical) {
          mapped.set(assetId, canonical);
        }
      }
      return mapped;
    }, [
      canonicalByAssetCanonicalId,
      normalizedAssetIds,
      routeCandidatesByAssetId,
    ]),
    canonicalByAssetCanonicalId,
    canonicalById,
    canonicalBySymbol: useMemo(() => {
      const mapped = new Map<string, AssetUniverseCanonicalModel>();
      for (const [symbol, link] of symbolLinkBySymbol) {
        const canonical = canonicalByAssetCanonicalId.get(
          link.assetCanonicalId,
        );
        if (canonical) {
          mapped.set(symbol, canonical);
        }
      }
      return mapped;
    }, [canonicalByAssetCanonicalId, symbolLinkBySymbol]),
    clearError: collectionError.clearError,
    error: resolveCollectionErrorPayload({
      error: hydrationQuery.error ?? collectionError.lastError,
      fallbackMessage: "Failed to load asset universe data.",
      isError: hydrationQuery.isError || collectionError.isError,
    }),
    errorCount: collectionError.errorCount,
    isError: hydrationQuery.isError || collectionError.isError,
    isLoading:
      (shouldHydrate ? hydrationQuery.isLoading : false) ||
      canonicalQuery.isLoading ||
      productQuery.isLoading ||
      assetIdRouteQuery.isLoading ||
      valuationQuery.isLoading ||
      symbolRouteQuery.isLoading ||
      symbolLinkQuery.isLoading,
    productByProductId,
    routeCandidatesByAssetId,
    routesByCanonicalId,
    status: shouldHydrate ? hydrationQuery.status : "success",
    symbols: normalizedSymbols,
    symbolLinkBySymbol,
    tokenizedEquityValuationsByAssetId: useMemo(
      () => mapTokenizedEquityValuationsByAssetId(valuationRows),
      [valuationRows],
    ),
  };
}
